/**
 * Add-entity dialog for the stacking plan — creates one of three structural
 * children of a building:
 *
 *   - `unit`         (Lot)   → attached to a floor (required)
 *   - `floor`        (Étage) → attached to the building
 *   - `common_area`  (Espace / partie commune) → attached to the building or a floor
 *
 * The kind is fixed by the caller (via the "Modifier" menu), so there is no
 * type picker — only the parent choice and an optional name.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { mdiLoading } from '@mdi/js'
import { AppIcon } from './ui/icon'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'
import { Button } from './ui/button'
import { Input } from './ui/input'
import {
  useAssetTypeByCode,
  useChildAssets,
  useCreateAsset,
  type Asset,
} from '../hooks/useQueries'

export type StackingEntityKind = 'unit' | 'floor' | 'common_area'

interface AddStackingEntityDialogProps {
  org: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The building the new entity belongs to. */
  buildingUid: string
  /** Which entity to create. */
  kind: StackingEntityKind
  onCreated?: (assetUid: string) => void
}

const floorLabel = (f: Asset) => f.name || f.uid.slice(0, 8)

export function AddStackingEntityDialog({
  org,
  open,
  onOpenChange,
  buildingUid,
  kind,
  onCreated,
}: AddStackingEntityDialogProps) {
  const { t } = useTranslation('assets')
  const queryClient = useQueryClient()

  const { data: unitType } = useAssetTypeByCode(org, 'unit')
  const { data: floorType } = useAssetTypeByCode(org, 'floor')
  const { data: commonAreaType } = useAssetTypeByCode(org, 'common_area')
  const { data: children = [] } = useChildAssets(org, buildingUid)

  const floors = useMemo<Asset[]>(() => {
    if (!floorType?.uid) return []
    return (children as Asset[]).filter((c) => c.asset_type_uid === floorType.uid)
  }, [children, floorType?.uid])

  const targetType =
    kind === 'unit' ? unitType : kind === 'floor' ? floorType : commonAreaType

  // A floor always hangs off the building — no parent picker. A lot needs a
  // floor. A common area can hang off the building (general parts) or a floor.
  const needsParentPicker = kind !== 'floor'
  const parentOptions = useMemo(() => {
    if (kind === 'unit') {
      return floors.map((f) => ({ uid: f.uid, label: floorLabel(f) }))
    }
    if (kind === 'common_area') {
      return [
        { uid: buildingUid, label: t('add_stacking_entity.parent_building') },
        ...floors.map((f) => ({ uid: f.uid, label: floorLabel(f) })),
      ]
    }
    return []
  }, [kind, floors, buildingUid, t])

  const [parentUid, setParentUid] = useState('')
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [parentError, setParentError] = useState(false)
  const nameInputRef = useRef<HTMLInputElement>(null)

  const createMutation = useCreateAsset(org)

  // Reset the transient form state once when the dialog opens. Deliberately
  // keyed only on `open` (not on `floors`/`kind`/`buildingUid`) — this must
  // NOT re-run on every incidental re-render while the dialog stays open,
  // or it wipes out whatever the user has already typed into the name
  // field. (Root cause of spec 2026-08-25-02: `extractData` used to hand
  // back a brand-new `[]` reference on every render for an empty children
  // list, which made the old single combined effect — keyed on the
  // `floors` array it derives from — re-fire on every render and clear
  // `name` right after each keystroke.)
  useEffect(() => {
    if (!open) return
    setName('')
    setError(null)
    setParentError(false)
  }, [open])

  // Default the parent picker once floor data is available. This is
  // separate from the reset above specifically so that a late-arriving (or,
  // if the array-identity bug above ever regresses, a spuriously-repeating)
  // `floors` update only ever touches `parentUid` — never the name field.
  useEffect(() => {
    if (!open) return
    if (kind === 'floor' || kind === 'common_area') {
      setParentUid(buildingUid)
    } else {
      setParentUid(floors.length > 0 ? floors[0].uid : '')
    }
  }, [open, kind, floors, buildingUid])

  // A lot can only be created once the building has at least one floor.
  const blockedNoFloors = kind === 'unit' && floors.length === 0

  const handleSubmit = async () => {
    setError(null)
    const resolvedParent = kind === 'floor' ? buildingUid : parentUid
    if (needsParentPicker && !resolvedParent) {
      setParentError(true)
      return
    }
    if (!targetType?.uid) {
      setError(t('add_stacking_entity.error_type_missing'))
      return
    }
    setSubmitting(true)
    try {
      const created = await createMutation.mutateAsync({
        name: name.trim() || t(`add_stacking_entity.default_name_${kind}`),
        asset_type_uid: targetType.uid,
        parent_asset_uid: resolvedParent,
      } as never)
      const createdUid = (created as { uid?: string } | undefined)?.uid

      // Refresh any child-asset listing so the new entity shows up in the plan.
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey as unknown[]
          return key[0] === 'get' && key[1] === '/api/v1/organizations/{orgCode}/assets'
        },
      })

      if (createdUid && onCreated) onCreated(createdUid)
      onOpenChange(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('add_stacking_entity.error_failed'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        data-testid="add-stacking-entity-dialog"
        // Radix's default open-autofocus picks the *first* tabbable element
        // in the content, which for lot/space is the parent <select>, not
        // the name input — and it can also lose the race against the
        // triggering DropdownMenuItem's close sequence (see
        // onCloseAutoFocus on the DropdownMenuContent in StackingPlan.tsx).
        // Take over the autofocus explicitly so the name input reliably
        // gets real document focus, not just a lingering focus-visible
        // style, as soon as the dialog opens.
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          nameInputRef.current?.focus()
        }}
      >
        <DialogHeader>
          <DialogTitle>{t(`add_stacking_entity.title_${kind}`)}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Parent — for lots (floor, required) and common areas (building/floor) */}
          {needsParentPicker && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t(`add_stacking_entity.parent_label_${kind}`)}
                {kind === 'unit' && <span className="text-destructive"> *</span>}
              </label>
              <select
                value={parentUid}
                onChange={(e) => {
                  setParentUid(e.target.value)
                  setParentError(false)
                }}
                disabled={submitting || blockedNoFloors}
                data-testid="add-stacking-entity-parent-select"
                className="block w-full px-3 py-2 border border-input bg-background text-foreground rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
              >
                {kind === 'unit' && (
                  <option value="">{t('add_stacking_entity.parent_placeholder')}</option>
                )}
                {parentOptions.map((o) => (
                  <option key={o.uid} value={o.uid}>
                    {o.label}
                  </option>
                ))}
              </select>
              {blockedNoFloors && (
                <p className="mt-1 text-xs text-muted-foreground">{t('add_stacking_entity.no_floors')}</p>
              )}
              {parentError && (
                <p className="mt-1 text-xs text-destructive">{t('add_stacking_entity.error_parent_required')}</p>
              )}
            </div>
          )}

          {/* Name — optional */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t(`add_stacking_entity.name_label_${kind}`)}
            </label>
            <Input
              ref={nameInputRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t(`add_stacking_entity.name_placeholder_${kind}`)}
              disabled={submitting}
              data-testid="add-stacking-entity-name-input"
            />
          </div>

          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="mt-6">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t('add_stacking_entity.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || blockedNoFloors}
            data-testid="add-stacking-entity-submit"
          >
            {submitting ? (
              <>
                <AppIcon path={mdiLoading} size="sm" className="mr-2 motion-safe:animate-spin" />
                {t('add_stacking_entity.submitting')}
              </>
            ) : (
              t('add_stacking_entity.submit')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
